import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RatingStarsProps {
  rating: number
  size?: 'sm' | 'md' | 'lg'
  showValue?: boolean
  className?: string
}

const sizeMap = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

export function RatingStars({
  rating,
  size = 'md',
  showValue = false,
  className,
}: RatingStarsProps) {
  const clampedRating = Math.max(0, Math.min(5, rating))
  const sizeClass = sizeMap[size]

  return (
    <span className={cn('inline-flex items-center gap-0.5', className)}>
      {Array.from({ length: 5 }, (_, i) => {
        const starIndex = i + 1
        const filled = clampedRating >= starIndex
        const half =
          !filled && clampedRating >= starIndex - 0.5

        return (
          <span key={i} className="relative inline-flex items-center justify-center">
            {/* Empty star base */}
            <Star
              className={cn(sizeClass, 'text-gray-300')}
              fill="currentColor"
              strokeWidth={0}
            />
            {/* Filled overlay — full or half */}
            {(filled || half) && (
              <span
                className={cn(
                  'absolute inset-0 overflow-hidden',
                  half ? 'w-1/2' : 'w-full',
                )}
              >
                <Star
                  className={cn(sizeClass, 'text-orange-400')}
                  fill="currentColor"
                  strokeWidth={0}
                />
              </span>
            )}
          </span>
        )
      })}

      {showValue && (
        <span
          className={cn(
            'ml-1 font-medium text-gray-600',
            size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base',
          )}
        >
          ({clampedRating.toFixed(1)})
        </span>
      )}
    </span>
  )
}
